/**
 * รายงานรากเทียม — all of the report's logic, pure and testable.
 *
 * Two consumers:
 *   tab 1  src/pages/reports/implant/GapQueue.jsx      ← PRESETS
 *   tab 2  src/pages/reports/implant/ConsultTrack.jsx  ← deriveStatus / summarize
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE STATUS IS DERIVED HERE AND NOT IN XANO
 * ─────────────────────────────────────────────────────────────────────────────
 * Xano api 651 returns FACTS — attended, won_date, promo_date, fixture_date,
 * prior_implant. The Thai status is computed here so MATURITY_DAYS lives in ONE
 * unit-tested place and can be changed without a Xano redeploy. Same split as
 * api 649 → src/lib/newcase.js.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🚨 180 DAYS IS NOT A GUESS AND A SHORTER WINDOW IS NOT "CONSERVATIVE"
 * ─────────────────────────────────────────────────────────────────────────────
 * Measured over 756 implant-consult patients since 2024
 * (implant/analysis/2026-08-31-implant-sales/data/FINDINGS.md §2b):
 *      ≤2 weeks   4.4%
 *      2wk–2mo    8.9%
 *      2–6mo      8.5%
 *      >6mo       2.9%
 * 82% of everyone who eventually buys takes longer than two weeks. A 14-day rule
 * calls four out of five real wins losses. Do not "tighten" this.
 *
 * 🚨 AND AN IMMATURE COHORT IS NEVER "หลุด". A consult booked last month has a
 * mathematically guaranteed loss verdict under any matured-window rule. Excluding
 * immature rows from BOTH sides of the rate is what stops this page re-creating
 * the retracted "collapsing conversion" finding — which was itself produced by
 * unequal look-forward windows. `conversionRate` prints its own denominator for
 * exactly that reason.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DATES
 * ─────────────────────────────────────────────────────────────────────────────
 * Every date on an api 651 row is a real Postgres `date` and already holds the
 * Bangkok calendar day — no conversion, no epoch arithmetic. `todayISO` is
 * passed IN by the page as bkkTodayISO(), never read from the browser: browser-
 * local and Bangkok today diverge for seven hours of every UTC day, which is the
 * whole reason commit 6b7bb88 exists.
 *
 * bkkDayISO / bkkTodayISO / fmtDay are re-exported from ./newcase.js. Do NOT
 * import the look-alikes in ./datetime.js — its toISO goes through Intl and its
 * callTs SUBTRACTS 7h, so the two silently produce different days.
 */
export { bkkDayISO, bkkTodayISO, fmtDay } from './newcase.js'

/** The maturity window, in days. See the header before changing it. */
export const MATURITY_DAYS = 180

/** The windows the page offers. 180 is the default and the only measured one. */
export const MATURITY_CHOICES = [60, 90, 180]

/* ── tab 1 · the call lists ─────────────────────────────────────────────────
 * `size` is NEVER hardcoded — every bucket in api 650's CASE is CURRENT_DATE-
 * relative, so G ages into C, C into D, D into F and F silently into X week by
 * week, and new extractions enter at the top the morning after (no healing gate
 * since 2026-10-28). The page reads total_rows on every load. The numbers below
 * are provenance only.
 *
 * X is deliberately absent. api 650 accepts list=X so the population is not
 * lost, but functions/api/implant/gap.js 403s it for anyone who is not gm/admin
 * and there is no button here — plan.md §4.3 item 7 asks for a lock, and
 * "ประโยคในเอกสารไม่ใช่ล็อก ตอนพฤศจิกายนที่รายชื่อเธอเริ่มบาง".
 */
export const PRESETS = [
  {
    // Ram, 2026-10-29 (implant/report.md §6g): every implant consult that did not buy —
    // walk-ins and gap-list patients alike — is a WARM lead, listed first and marked
    // Warm Case everywhere. api 678 builds it live; it starts at พร้อมโทร with no review.
    id: 'W',
    label: 'W · ปรึกษาแล้วยังไม่ตัดสินใจ',
    warm: true,
    hint: 'Warm Case — มาปรึกษารากเทียมแล้ว ยังไม่ชำระ และยังไม่มีนัดต่อ · โทรติดตามแผนที่หมอเสนอ ไม่ต้องรีวิวก่อน',
    refDateLabel: 'ปรึกษาครั้งล่าสุด',
    inQuarter: true,
    sortNote: 'นัดหลุดก่อน แล้วปรึกษาแล้วยังไม่จ่าย · ปรึกษาล่าสุดอยู่บน',
    provenance: 80,
  },
  {
    id: 'A',
    label: 'A · มีนัดอยู่แล้ว',
    hint: 'มีช่องว่างและมีนัดอยู่แล้ว — เดินเข้ามาเองอยู่แล้ว',
    refDateLabel: 'ถอนฟันครั้งล่าสุด',
    inQuarter: true,
    sortNote: 'เรียงตามวันนัดที่ใกล้ที่สุด — คุยกับเขาก่อนวันที่เขาจะมา',
    provenance: 64,
  },
  {
    // COPIED from /follow-ups/recall on Ram's call, 2026-09-17 — that page keeps its
    // own filter too. api 650: status 'รากเทียม' AND last_recall IS NULL, active.
    // Any recall note from either page sets last_recall, so one call of any kind
    // moves the patient out of B, exactly as it does there.
    id: 'B',
    label: 'B · ติดป้ายรากเทียม',
    hint: 'คนไข้ที่ถูกตั้งสถานะ “รากเทียม” และยังไม่เคยมีใครโทร — ชุดเดียวกับในหน้า ติดตาม/Recall · โทรแล้วจะออกจากลิสต์นี้',
    refDateLabel: 'รักษาครั้งล่าสุด',
    inQuarter: true,
    provenance: 407,
  },
  {
    // Ram, 2026-10-28: the first six months start at day 0 and split in two.
    // api 650: G = ref_date within 30 days, C = 31–180. Sizes: build 279114,
    // before call-time exclusions.
    id: 'G',
    label: 'G · ถอนฟันภายใน 1 เดือน',
    hint: 'ถอนฟันมาไม่เกิน 30 วัน — รายชื่อใหม่ที่สุด เข้าลิสต์เช้าวันถัดจากวันถอน',
    refDateLabel: 'ถอนฟันครั้งล่าสุด',
    inQuarter: true,
    provenance: 48,
  },
  {
    id: 'C',
    label: 'C · ถอนฟัน 2–6 เดือน',
    hint: 'ถอนฟันมา 31–180 วัน · แผลหายพอดี เป็นจังหวะที่คุยเรื่องรากเทียมได้',
    refDateLabel: 'ถอนฟันครั้งล่าสุด',
    inQuarter: true,
    provenance: 319,
  },
  {
    id: 'E',
    label: 'E · ใส่ฟันปลอมถอดได้',
    hint: 'เคสใหญ่ที่สุด — คนที่ใส่ฟันปลอมถอดได้และยังไม่เคยทำรากเทียม',
    refDateLabel: 'ใส่ฟันปลอมครั้งล่าสุด',
    inQuarter: true,
    provenance: 349,
  },
  {
    id: 'D',
    label: 'D · ถอนฟัน 6–12 เดือน',
    hint: 'แผนไตรมาสนี้ตัด D ออก (plan §4.3) — เปิดไว้ให้เลือกได้ แต่ไม่ใช่คิวหลัก',
    refDateLabel: 'ถอนฟันครั้งล่าสุด',
    inQuarter: false,
    provenance: 643,
  },
  {
    id: 'F',
    label: 'F · ถอนฟัน 1–2 ปี',
    hint: 'แผนไตรมาสนี้ตัด F ออก (plan §4.3) แต่ playbook §3.4 บอกให้เก็บไว้เดือน 3',
    refDateLabel: 'ถอนฟันครั้งล่าสุด',
    inQuarter: false,
    provenance: 1747,
  },
  {
    // Ram, 2026-10-30 (implant/report.md §6h, plan B): the cold step after Warm Case — W's
    // rule with the latest consult 181-365 days ago, for a patient no extraction / denture
    // list holds. No review; api 678 sorts it after every other list. ~6% of consults still
    // unbought at 6 months buy in the next 6 (first consults Jun 2023-Apr 2025).
    id: 'L',
    label: 'L · ปรึกษาเกิน 6 เดือน',
    hint: 'มาปรึกษารากเทียมเมื่อ 6–12 เดือนก่อน ยังไม่ชำระ ยังไม่มีนัด และไม่อยู่ในลิสต์ถอนฟัน · โทรหลังลิสต์อื่น ไม่ต้องรีวิว',
    refDateLabel: 'ปรึกษาครั้งล่าสุด',
    inQuarter: false,
    sortNote: 'อยู่ท้ายสุดของรอโทร · ปรึกษาล่าสุดอยู่บน',
    provenance: 88,
  },
]

export const PRESET_IDS = PRESETS.map((p) => p.id)
export const presetById = (id) => PRESETS.find((p) => p.id === id) || null

/** A persisted preset id from an older build must not throw. */
export const safePreset = (id) => (presetById(id) ? id : 'C')

/* ── แท็บรอโทร · every type at once (api 678 lists Q / T, 2026-10-29) ─────────
 * Ram: the call tab shows every พร้อมโทร patient by default, the type is a filter (not a
 * tab) and a ประเภท column says what each row is. Q = every call list (W A B G C D E F L),
 * T = every extraction age (G C D F); api 678 shows one row per patient for both. */
export const COMBINED = {
  Q: { id: 'Q', label: 'ทุกประเภท', hint: 'คนไข้ที่พร้อมโทรทุกประเภท · นัดหลุดและ Warm Case ขึ้นก่อน · ปรึกษาเกิน 6 เดือนอยู่ท้ายสุด', refDateLabel: 'วันที่อ้างอิง' },
  T: { id: 'T', label: 'ถอนฟันแล้ว · ทุกช่วง', hint: 'ถอนฟันที่คลินิกภายใน 2 ปี ทุกช่วงเวลา', refDateLabel: 'ถอนฟันครั้งล่าสุด' },
}
export const EXTRACTION_LISTS = ['G', 'C', 'D', 'F']
// A row's ประเภท (api 678 `list`) → words; the four extraction ages are one type with an
// age. `ref` = what the row's date is, for the combined list's date column.
export const LIST_TYPE = {
  W: { type: 'Warm Case', ref: 'ปรึกษา' },
  A: { type: 'มีนัดแล้ว', ref: 'ถอนฟัน' },
  G: { type: 'ถอนฟันแล้ว', age: 'ภายใน 1 เดือน', ref: 'ถอนฟัน' },
  C: { type: 'ถอนฟันแล้ว', age: '2–6 เดือน', ref: 'ถอนฟัน' },
  D: { type: 'ถอนฟันแล้ว', age: '6–12 เดือน', ref: 'ถอนฟัน' },
  F: { type: 'ถอนฟันแล้ว', age: '1–2 ปี', ref: 'ถอนฟัน' },
  E: { type: 'ใส่ฟันปลอม', ref: 'ใส่ฟันปลอม' },
  B: { type: 'ติดป้ายรากเทียม', ref: 'รักษา' },
  L: { type: 'ปรึกษาแล้ว', age: 'เกิน 6 เดือน', ref: 'ปรึกษา' },
}
/** แท็บรอโทร's saved choice: Q, T or a call list; anything else (an older build's) → Q. */
export const safeCallList = (id) => (COMBINED[id] || LIST_TYPE[id] ? id : 'Q')

/* ── tab 1 · the views across lists (api 678, 2026-10-29) ────────────────────
 * Not call lists: nobody is called from them, so opening a row never claims the
 * lock, and the choice is never persisted — a caller who reopens the page lands on
 * a call list, never on the people not to call. */
export const VIEWS = [
  {
    id: 'H',
    label: 'รอวันนัด',
    hint: 'มีนัดรากเทียม / ปลูกกระดูก / ฟันปลอม / สะพานฟันอยู่แล้ว — ไม่ต้องโทร · ถ้านัดหลุดหรือมาแล้วไม่จ่าย จะกลับเข้าลิสต์เอง',
    refDateLabel: 'ถอนฟัน / ปรึกษาครั้งล่าสุด',
  },
  {
    id: 'P',
    label: 'พักไว้',
    hint: 'โทรแล้วตอบว่ายังไม่ตอนนี้ (ยังไม่ตัดสินใจ · ราคา · กลัว · ไม่มีเวลา · ถามที่บ้าน · ไม่เดือดร้อน) — กลับเข้าลิสต์เป็นพร้อมโทรเองเมื่อครบ 6 เดือน',
    refDateLabel: 'ถอนฟัน / ปรึกษาครั้งล่าสุด',
  },
  {
    id: 'R',
    label: 'พักจนกว่าจะมีรอบใหม่',
    hint: 'ไม่ผ่านรีวิว · เคสไม่เหมาะ · ไปทำที่อื่นแล้ว — กลับมาเมื่อถอนฟันซี่ใหม่ หรือมาปรึกษารากเทียมใหม่',
    refDateLabel: 'ถอนฟัน / ปรึกษาครั้งล่าสุด',
  },
]
export const viewById = (id) => VIEWS.find((v) => v.id === id) || null

// api 678 `stage` — what the appointment book says about the patient's round.
export const STAGES = {
  waiting:          { label: 'รอวันนัด', tone: 'slate' },
  missed:           { label: 'นัดหลุด', tone: 'rose' },
  consulted_unpaid: { label: 'ปรึกษาแล้ว ยังไม่จ่าย', tone: 'amber' },
  prep_done:        { label: 'ปลูกกระดูกแล้ว รอฝังราก', tone: 'brand' },
  back_from_park:   { label: 'กลับจากพัก', tone: 'brand' },
  reopened:         { label: 'ดึงกลับมาโทร', tone: 'brand' },
  // ไม่รับสาย / จองแล้ว-no-appointment, back after the 3-day wait (api 678, 2026-10-29)
  retry:            { label: 'โทรซ้ำ', tone: 'brand' },
}

// The appointment types api 678 treats as implant-related (plan page §5).
export const HOLD_TYPE_LABEL = {
  35: 'ปรึกษารากเทียม', 83: 'ปรึกษา ส่วนศัลยกรรม', 84: 'ปรึกษา ส่วน Prosth',
  44: 'ฝังรากเทียม', 45: 'ครอบฟันบนราก', 70: 'รากเทียม อื่นๆ',
  26: 'ปลูกกระดูก', 75: 'ปลูกกระดูก (SPEC)', 21: 'ยกไซนัส', 74: 'ยกไซนัส (SPEC)',
  37: 'ฟันปลอม', 40: 'สะพานฟัน',
}

/** api 678 park_reason → words. A chip code reads as the chip; FAIL is a review. */
export function parkReasonLabel(reason, outcomeByCode = {}) {
  if (!reason) return ''
  if (reason === 'FAIL') return 'ไม่ผ่านรีวิว'
  return outcomeByCode[reason]?.thai || reason
}

/** Whole days from an ISO day until today (Bangkok), never negative. */
export function daysSince(iso, todayISO) {
  const d = daysBetween(iso, todayISO)
  return d == null ? null : Math.max(0, d)
}

/**
 * The W follow-up rhythm already in the tab 2 spec: day 21, 45 and 90 after the
 * consult. Returns the next point and how far off it is, or 'over' past day 90.
 */
export function cadenceHint(daysSinceConsult) {
  if (daysSinceConsult == null || daysSinceConsult === '') return null   // Number(null) is 0
  const d = Number(daysSinceConsult)
  if (!Number.isFinite(d)) return null
  for (const point of [21, 45, 90]) if (d < point) return { point, inDays: point - d }
  return { point: null, inDays: null, over: true }
}

/* ── tab ผลการโทร · the calls chart ─────────────────────────────────────────*/

// calls = api 680's per-day calls (every type); cold / warm are api 679's split
export const TREND_KEYS = ['calls', 'cold', 'warm', 'reached', 'booked', 'booked_cold', 'paid']
const TREND_DAILY_MAX = 45   // longer periods are drawn per week (Monday start)

const addDaysISO = (iso, n) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10)

/**
 * api 679 `trend` (one row per Bangkok day WITH activity) → one bucket per day of the
 * period, empty days filled with zeros so a day nobody called shows as a gap. The period
 * matches the SQL: today − days … today; days = 0 (ทั้งหมด) starts at the first row.
 * Past 45 days the buckets are Monday weeks, each { start, end, weekly: true }.
 */
export function trendBuckets(rows, { days = 0, today }) {
  const list = (Array.isArray(rows) ? rows : []).filter((r) => r?.d)
  const first = list.reduce((m, r) => (r.d < m ? r.d : m), today)
  const start = Number(days) > 0 ? addDaysISO(today, -Number(days)) : first
  const span = daysBetween(start, today) + 1
  const weekly = span > TREND_DAILY_MAX
  const bucketOf = (iso) => {
    if (!weekly) return iso
    const dow = new Date(`${iso}T00:00:00Z`).getUTCDay()   // 0 = Sunday
    return addDaysISO(iso, -((dow + 6) % 7))
  }
  const out = new Map()
  for (let d = bucketOf(start); d <= today; d = addDaysISO(d, weekly ? 7 : 1)) {
    const b = bucketOf(d)
    if (!out.has(b)) out.set(b, { start: b, end: weekly ? addDaysISO(b, 6) : b, weekly, ...Object.fromEntries(TREND_KEYS.map((k) => [k, 0])) })
  }
  for (const r of list) {
    const bucket = r.d >= start && out.get(bucketOf(r.d))
    if (!bucket) continue   // before the period (a BK-confirmed earlier booking)
    for (const k of TREND_KEYS) bucket[k] += Number(r[k]) || 0
  }
  return [...out.values()]
}

/* ── dates ──────────────────────────────────────────────────────────────────*/

/** Tolerant ISO-day reader: 'YYYY-MM-DD', a full ISO string, or epoch ms. */
export function toISODate(v) {
  if (v == null || v === '') return null
  if (typeof v === 'number') return new Date(v).toISOString().slice(0, 10)
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  if (/^\d{10,}$/.test(s)) return new Date(Number(s)).toISOString().slice(0, 10)
  const t = Date.parse(s)
  return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : null
}

/** Whole days from `a` to `b`, both ISO days. Negative when b precedes a. */
export function daysBetween(a, b) {
  const x = toISODate(a)
  const y = toISODate(b)
  if (!x || !y) return null
  return Math.round((Date.parse(`${y}T00:00:00Z`) - Date.parse(`${x}T00:00:00Z`)) / 86400000)
}

/* ── tab 2 · the derived ladder ─────────────────────────────────────────────*/

export const STATUS = {
  upcoming:  { label: 'ยังไม่ถึงวันนัด',      tone: 'slate',   derived: false },
  won:       { label: 'ปิดได้',              tone: 'emerald', derived: true  },
  won_late:  { label: 'ปิดการขาย (เกินกรอบ)', tone: 'emerald', derived: true  },
  // เตรียมตัว (Ram, 2026-10-30): no closing bill yet, but a paid pre-implant step (Stent,
  // graft, sinus lift, Digital Guide — api 651 prep_date) OR the next implant-related step
  // booked (booked_date). They have decided; the clinic is preparing. Not ยังไม่ตัดสินใจ,
  // which means nothing paid and nothing booked — the patients the call list holds.
  prep:      { label: 'เตรียมตัว',             tone: 'violet',  derived: true  },
  noshow:    { label: 'ไม่มาตามนัด',          tone: 'amber',   derived: true  },
  open:      { label: 'ยังไม่ตัดสินใจ',        tone: 'sky',     derived: true  },
  lost:      { label: 'หลุด',                tone: 'slate',   derived: true  },
}

export const STATUS_ORDER = ['won', 'won_late', 'prep', 'open', 'noshow', 'lost', 'upcoming']

/** What a won consult bought — api 651 won_kind, the first closing bill (2026-10-30). */
export const WON_KIND = { implant: 'รากเทียม', denture: 'ฟันปลอม', bridge: 'สะพานฟัน' }

/* ── what the calls found, and where the patient is now (ผลเคสปรึกษา, 2026-10-30) ──
 * api 651 returns the facts api 678 parks a patient on (p_*); this applies api 678's
 * PARKING CASE (pq) to them — change one, change the other:
 *   P  CN PR FE (+ retired TM FM NU) in this round, under 6 months old → พักไว้ถึง
 *   R  NO OC DNC in this round, or a review ไม่ผ่าน → ไม่โทรแล้วในรอบนี้
 *   N  NA IV BK in this round, under 3 days old → โทรซ้ำ (date)
 * each undone by a later ดึงกลับมาโทร (reopen at or after it). Then whether a call list
 * still holds them: the consult lists (W ≤ 180 days, ปรึกษาเกิน 6 เดือน ≤ 365) unless a
 * closing bill or ortho, or a snapshot gap / denture row, or list B. */
export const PARK_P_CODES = ['CN', 'PR', 'FE', 'TM', 'FM', 'NU']
export const PARK_R_CODES = ['NO', 'OC', 'DNC']
export const PARK_N_CODES = ['NA', 'IV', 'BK']
export const CONSULT_LIST_DAYS = 365

const DAY_MS = 86400000
// a Bangkok calendar day + N months / days, as an ISO day (api 678: + interval)
function bkkPlus(ms, { months = 0, days = 0 }) {
  const d = new Date(Number(ms) + 7 * 3600000)
  d.setUTCMonth(d.getUTCMonth() + months)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export const CALL_STATE = {
  cancelled: 'ยกเลิกเคส',
  waiting:   'รอวันนัด',
  parked:    'พักไว้ถึง',
  retry:     'โทรซ้ำ',
  out:       'ไม่โทรแล้วในรอบนี้',
  queue:     'ในรอโทร',
  queue_new: 'ในรอโทร · ยังไม่เคยโทร',
  off:       'ไม่อยู่ในลิสต์โทร',
}

/** The R park of api 678 (no clock involved): NO / OC / DNC in this round, or a failed
 *  review — each undone by a later bring-back — or patient.status ยกเลิกเคส. */
export function callClosed(r) {
  if (!r) return false
  if (r.patient_status === 'ยกเลิกเคส') return true
  const at = Number(r.p_call_at) || 0
  const inRound = at > 0 && at >= (Number(r.round_start_ms) || 0)
  const notReopened = (t) => r.p_reopen_at == null || Number(r.p_reopen_at) < Number(t)
  if (inRound && notReopened(at) && PARK_R_CODES.includes(String(r.p_call_code || ''))) return true
  return !!(r.p_review_fail && r.p_review_at != null && notReopened(r.p_review_at))
}

/** { code, label, until? } for one api 651 row. `nowMs` for tests. */
export function callState(r, todayISO, nowMs = Date.now()) {
  const st = (code, until) => ({ code, label: CALL_STATE[code], until: until || null })
  if (!r) return st('off')
  if (r.patient_status === 'ยกเลิกเคส') return st('cancelled')
  if (r.booked_date) return st('waiting', toISODate(r.booked_date))

  const start = Number(r.round_start_ms) || 0
  const at = Number(r.p_call_at) || 0
  const code = String(r.p_call_code || '')
  const inRound = at > 0 && at >= start
  const notReopened = (t) => r.p_reopen_at == null || Number(r.p_reopen_at) < Number(t)
  if (inRound && notReopened(at)) {
    // api 678: to_timestamp(chip_at) > now() - interval '6 months'
    const sixAgo = new Date(nowMs)
    sixAgo.setUTCMonth(sixAgo.getUTCMonth() - 6)
    if (PARK_P_CODES.includes(code) && at > sixAgo.getTime()) return st('parked', bkkPlus(at, { months: 6 }))
    if (PARK_R_CODES.includes(code)) return st('out')
    if (PARK_N_CODES.includes(code) && at > nowMs - 3 * DAY_MS) return st('retry', bkkPlus(at, { days: 3 }))
  }
  if (r.p_review_fail && r.p_review_at != null && notReopened(r.p_review_at)) return st('out')

  const age = daysBetween(r.latest_consult, todayISO)
  const onConsultList = age != null && age >= 0 && age <= CONSULT_LIST_DAYS && !r.p_won && !r.p_ortho
  if (!(onConsultList || r.p_on_list || r.p_on_b)) return st('off')
  return st(inRound ? 'queue' : 'queue_new')
}


/**
 * ⚠️ The tooltip that MUST accompany every ไม่มาตามนัด cell. The CRM's own
 * status dropdown offers ไม่มาตามนัด today and Xano's enum rejects the write, so
 * a bare "ไม่มาตามนัด" here would be read as a recorded fact rather than a
 * calculation. See api 651's header.
 */
export const NOSHOW_NOTE =
  'รวมนัดที่ยกเลิกล่วงหน้า (สถานะ X) · ระบบไม่มีช่องบันทึก "ไม่มาตามนัด" — ที่เหลือคำนวณจาก "ถึงวันนัดแล้วแต่ไม่มีรายการรักษาในวันนั้น"'

/** Has this consult been given its full follow-up window yet? */
export function isMature(row, todayISO, maturity = MATURITY_DAYS) {
  const d = daysBetween(row?.date, todayISO)
  return d != null && d >= maturity
}

/** Days from the consult to the first closing bill (implant / denture / bridge), or null. */
export function daysToWin(row) {
  return row?.won_date ? daysBetween(row.date, row.won_date) : null
}

/**
 * The Thai status of one consult. Order is load-bearing:
 *   • a future date wins over everything (nothing has happened yet);
 *   • X (cancelled in advance) IS a no-show (Ram, 2026-10-30: "it's the same") — it
 *     used to be its own status, ยกเลิกล่วงหน้า, that beat everything and stayed out of
 *     the rate. A future X is a no-show too (it will not happen), never upcoming;
 *   • money beats attendance, so someone who skipped or cancelled the consult and
 *     bought two months later is a WIN, not a no-show;
 *   • a win is a CLOSING bill (implant, denture or bridge — api 651 since 2026-10-30,
 *     the same rule as Warm Case); a paid pre-implant step or a booked next step is
 *     เตรียมตัว — money and the appointment book both beat attendance;
 *   • หลุด requires maturity — unless the calls closed the round (callClosed: the patient
 *     said ไปทำที่อื่นแล้ว / เคสไม่เหมาะ, the review failed, or ยกเลิกเคส). Otherwise an
 *     immature row is ยังไม่ตัดสินใจ, never lost.
 */
export function deriveStatus(row, todayISO, maturity = MATURITY_DAYS) {
  const date = toISODate(row?.date)
  const today = toISODate(todayISO)
  const sinceConsult = daysBetween(date, today)
  const status = String(row?.status ?? '').trim()

  if (sinceConsult != null && sinceConsult <= 0 && status !== 'X') return code('upcoming', sinceConsult)

  const lag = daysToWin(row)
  if (lag != null && lag >= 0) return code(lag <= maturity ? 'won' : 'won_late', sinceConsult, lag)
  if (row?.prep_date || row?.booked_date) return code('prep', sinceConsult)
  // the calls closed it (2026-10-30): ไปทำที่อื่นแล้ว / เคสไม่เหมาะ, a failed review or
  // ยกเลิกเคส — off every call list in this round, so not "undecided" whatever its age
  if (callClosed(row)) return code('lost', sinceConsult)

  if (!row?.attended || status === 'X') return code('noshow', sinceConsult)
  if (!isMature(row, today, maturity)) return code('open', sinceConsult)
  return code('lost', sinceConsult)

  function code(c, days, lagDays = null) {
    return { code: c, ...STATUS[c], days: days == null ? null : Math.max(0, days), lagDays }
  }
}

/**
 * The headline conversion rate.
 *
 * PER PATIENT, not per appointment: someone with three consults across two years
 * is one lost opportunity, not three. Each patient is represented by their
 * EARLIEST consult in the window, so overlapping maturity windows cannot
 * double-count one sale.
 *
 * Excluded from BOTH numerator and denominator:
 *   • immature cohorts     — they have not been given the window yet
 *   • prior_implant rows   — the patient was already mid-plan when they booked,
 *                            so their purchase is not this consult's win
 * A cancelled (X) consult COUNTS, like a no-show (Ram, 2026-10-30). It used to be left
 * out; on 2025-10-30..2026-10-29 that moved the rate 42/146 = 28.8% → 44/178 = 24.7%
 * (32 cancelled first consults join the denominator, 2 of them bought within 180 days).
 *
 * Returns the denominator alongside the rate ON PURPOSE. The tile prints it.
 */
export function conversionRate(rows, todayISO, maturity = MATURITY_DAYS) {
  const first = firstPerPatient(rows)
  let denom = 0
  let won = 0
  let wonImplant = 0
  let immature = 0
  for (const r of first) {
    const st = deriveStatus(r, todayISO, maturity)
    if (r.prior_implant) continue
    if (!isMature(r, todayISO, maturity)) { immature += 1; continue }
    denom += 1
    if (st.code === 'won') {
      won += 1
      // api 651 before won_kind: every win was an implant bill
      if ((r.won_kind || 'implant') === 'implant') wonImplant += 1
    }
  }
  // wonImplant: the same wins read as "ended in an implant" — the rest bought a denture or
  // a bridge (Ram, 2026-10-30: count them as won, but keep the implant share readable)
  return { rate: denom ? won / denom : null, won, wonImplant, denom, immature }
}

/** One row per patient — their earliest consult in the window. */
export function firstPerPatient(rows) {
  const best = new Map()
  for (const r of rows || []) {
    const k = String(r?.patient_id ?? '')
    if (!k) continue
    const cur = best.get(k)
    if (!cur) { best.set(k, r); continue }
    const a = toISODate(r.date) || ''
    const b = toISODate(cur.date) || ''
    if (a < b || (a === b && Number(r.id) < Number(cur.id))) best.set(k, r)
  }
  return [...best.values()]
}

/**
 * ส่วนคลินิก, counted ONCE PER PATIENT.
 *
 * 🚨 Never sum `won_clinic` across rows. It is per CONSULT — the implant money
 * inside that consult's own 180-day window — so a patient with two consults whose
 * windows overlap carries the same billing line on both rows. Summing it inflated
 * the tile by 30.5% on the matured 2024-09..2025-09 cohort: ฿3,973,987 against
 * ฿3,044,253 counted once (measured 2026-09-17; 57 of 282 patients repeat).
 *
 * `patient_won_clinic` is that patient's money inside ANY of their windows,
 * computed once by api 651 and repeated on each of their rows, so taking one
 * value per patient is exact. The `?? won_clinic` fallback only matters against
 * an api 651 older than that column; max() over it still cannot double-count.
 */
export function clinicOncePerPatient(rows) {
  const best = new Map()
  for (const r of rows || []) {
    const k = String(r?.patient_id ?? '')
    if (!k) continue
    const v = Number(r.patient_won_clinic ?? r.won_clinic) || 0
    best.set(k, Math.max(best.get(k) ?? 0, v))
  }
  let sum = 0
  for (const v of best.values()) sum += v
  return sum
}

/** Tiles across the top of tab 2. */
export function summarize(rows, todayISO, maturity = MATURITY_DAYS) {
  const list = rows || []
  const counts = Object.fromEntries(STATUS_ORDER.map((c) => [c, 0]))
  let attended = 0
  let noNote = 0
  const patients = new Set()
  for (const r of list) {
    counts[deriveStatus(r, todayISO, maturity).code] += 1
    if (r.attended) attended += 1
    // `details` is the note staff actually write; `remark` is near-unused
    // (948 vs 147 of 1,044 implant consults, measured 2026-09-08).
    if (!r.has_details) noNote += 1
    if (r.patient_id != null) patients.add(String(r.patient_id))
  }
  return {
    consults: list.length,
    patients: patients.size,
    attended,
    clinic: clinicOncePerPatient(list),
    noNote,
    counts,
    conversion: conversionRate(list, todayISO, maturity),
  }
}

/** Group tab 2's rows for the drill-down table. */
export const CONSULT_GROUPERS = {
  status: { noun: 'สถานะ',   key: (r, t, m) => deriveStatus(r, t, m).code, label: (k) => STATUS[k]?.label || k },
  branch: { noun: 'สาขา',    key: (r) => String(r.branch || '—'),          label: (k) => k },
  from:   { noun: 'ที่มา',    key: (r) => String(r.from || 'ไม่ระบุ'),        label: (k) => k },
  month:  { noun: 'เดือน',   key: (r) => (toISODate(r.date) || '').slice(0, 7), label: (k) => k },
}

export function groupConsults(rows, dim, todayISO, maturity = MATURITY_DAYS) {
  const g = CONSULT_GROUPERS[dim] ? dim : 'status'
  const { key, label } = CONSULT_GROUPERS[g]
  const m = new Map()
  for (const r of rows || []) {
    const k = key(r, todayISO, maturity)
    if (!m.has(k)) m.set(k, [])
    m.get(k).push(r)
  }
  const out = [...m.entries()].map(([k, rs]) => ({ key: k, label: label(k), count: rs.length, rows: rs }))
  // สถานะ keeps the ladder's order; every other axis sorts by size.
  if (g === 'status') out.sort((a, b) => STATUS_ORDER.indexOf(a.key) - STATUS_ORDER.indexOf(b.key))
  else out.sort((a, b) => b.count - a.count || String(a.key).localeCompare(String(b.key)))
  return out
}
